/**
 * Maps Meta ad group placement keys (see metaPlacements.js) to Meta Marketing API
 * ad preview format strings used by AdPreviewMeta and ModalEditPlacementsMeta.
 * Audience Network placements have no dedicated preview in the current UI — they
 * contribute no format here (manual-only selections may fall back to full list).
 */

import {
  ADS_CAMPAIGN_AD_FORMAT_DESKTOP_FEED_STANDARD,
  ADS_CAMPAIGN_AD_FORMAT_FACEBOOK_MOBILE_FEED_STANDARD,
  ADS_CAMPAIGN_AD_FORMAT_FACEBOOK_PROFILE_FEED_MOBILE,
  ADS_CAMPAIGN_AD_FORMAT_FACEBOOK_REELS_MOBILE,
  ADS_CAMPAIGN_AD_FORMAT_FACEBOOK_STORY,
  ADS_CAMPAIGN_AD_FORMAT_INSTAGRAM_EXPLORE_GRID_HOME,
  ADS_CAMPAIGN_AD_FORMAT_INSTAGRAM_EXPLORE_IMMERSIVE,
  ADS_CAMPAIGN_AD_FORMAT_INSTAGRAM_REEL,
  ADS_CAMPAIGN_AD_FORMAT_INSTAGRAM_STANDARD,
  ADS_CAMPAIGN_AD_FORMAT_INSTAGRAM_STORY,
  ADS_CAMPAIGN_AD_FORMAT_INSTAGRAM_PROFILE_FEED,
  ADS_CAMPAIGN_AD_FORMAT_INSTAGRAM_SEARCH_GRID,
  ADS_CAMPAIGN_AD_FORMAT_INSTREAM_VIDEO_MOBILE,
  ADS_CAMPAIGN_AD_FORMAT_MARKETPLACE_MOBILE,
  ADS_CAMPAIGN_AD_FORMAT_MESSENGER_MOBILE_INBOX_MEDIA,
  ADS_CAMPAIGN_AD_FORMAT_MESSENGER_MOBILE_STORY_MEDIA,
  ADS_CAMPAIGN_AD_FORMAT_RIGHT_COLUMN_STANDARD,
  ADS_CAMPAIGN_AD_FORMAT_SUGGESTED_VIDEO_DESKTOP,
} from "../constants";

/** @type {Record<string, string[]>} */
export const PLACEMENT_KEY_TO_AD_PREVIEW_FORMATS = {
  facebookFeed: [
    ADS_CAMPAIGN_AD_FORMAT_FACEBOOK_MOBILE_FEED_STANDARD,
    ADS_CAMPAIGN_AD_FORMAT_DESKTOP_FEED_STANDARD,
  ],
  facebookProfileFeed: [ADS_CAMPAIGN_AD_FORMAT_FACEBOOK_PROFILE_FEED_MOBILE],
  instagramFeed: [ADS_CAMPAIGN_AD_FORMAT_INSTAGRAM_STANDARD],
  facebookMarketplace: [ADS_CAMPAIGN_AD_FORMAT_MARKETPLACE_MOBILE],
  facebookRightColumn: [ADS_CAMPAIGN_AD_FORMAT_RIGHT_COLUMN_STANDARD],
  instagramExplore: [ADS_CAMPAIGN_AD_FORMAT_INSTAGRAM_EXPLORE_IMMERSIVE],
  instagramExploreHome: [ADS_CAMPAIGN_AD_FORMAT_INSTAGRAM_EXPLORE_GRID_HOME],
  messengerInbox: [ADS_CAMPAIGN_AD_FORMAT_MESSENGER_MOBILE_INBOX_MEDIA],
  facebookNotifications: [ADS_CAMPAIGN_AD_FORMAT_FACEBOOK_MOBILE_FEED_STANDARD],
  instagramStories: [ADS_CAMPAIGN_AD_FORMAT_INSTAGRAM_STORY],
  facebookStories: [ADS_CAMPAIGN_AD_FORMAT_FACEBOOK_STORY],
  messengerStories: [ADS_CAMPAIGN_AD_FORMAT_MESSENGER_MOBILE_STORY_MEDIA],
  instagramReels: [ADS_CAMPAIGN_AD_FORMAT_INSTAGRAM_REEL],
  instagramProfileReels: [ADS_CAMPAIGN_AD_FORMAT_INSTAGRAM_REEL],
  facebookReels: [ADS_CAMPAIGN_AD_FORMAT_FACEBOOK_REELS_MOBILE],
  facebookInstreamVideos: [ADS_CAMPAIGN_AD_FORMAT_INSTREAM_VIDEO_MOBILE],
  adsOnFacebookReels: [ADS_CAMPAIGN_AD_FORMAT_FACEBOOK_REELS_MOBILE],
  facebookSearchResults: [ADS_CAMPAIGN_AD_FORMAT_SUGGESTED_VIDEO_DESKTOP],
  instagramSearchResults: [ADS_CAMPAIGN_AD_FORMAT_INSTAGRAM_SEARCH_GRID],
  instagramProfileFeed: [ADS_CAMPAIGN_AD_FORMAT_INSTAGRAM_PROFILE_FEED],
};

/**
 * Modal customization row ids that differ from catalog placement keys.
 * @type {Record<string, string>}
 */
export const MODAL_PLACEMENT_ITEM_ID_TO_CATALOG_KEY = {
  facebookInstreamForVideos: "facebookInstreamVideos",
  facebookProfileFeed: "facebookProfileFeed",
  adsOnFacebookReels: "adsOnFacebookReels",
  audienceNetworkNativeBanner: "audienceNetworkNativeBannerInterstitial",
  audienceNetworkRewardedVideos: "audienceNetworkRewardedVideos",
};

/**
 * @param {unknown} placements - Ad group placements field from react-hook-form
 * @returns {string[] | null} Placement keys when manual; null if advantage or invalid
 */
export const getManualPlacementKeysFromForm = (placements) => {
  if (!placements || typeof placements !== "object") return null;
  if (placements.type !== "manual") return null;
  return [
    ...(placements.facebookPositions || []),
    ...(placements.instagramPositions || []),
    ...(placements.messengerPositions || []),
    ...(placements.audienceNetworkPositions || []),
  ];
};

/**
 * @param {string[]} placementKeys
 * @returns {Set<string>}
 */
export const getAdPreviewFormatSetForPlacementKeys = (placementKeys) => {
  const set = new Set();
  for (const key of placementKeys) {
    const fmts = PLACEMENT_KEY_TO_AD_PREVIEW_FORMATS[key];
    if (fmts) {
      fmts.forEach((f) => set.add(f));
    }
  }
  return set;
};

/**
 * @param {{ baseFormats: string[], placements: unknown }} params
 * @returns {string[]}
 */
export const filterFormatsByManualPlacements = ({
  baseFormats,
  placements,
}) => {
  const keys = getManualPlacementKeysFromForm(placements);
  if (keys === null || keys.length === 0) {
    return baseFormats;
  }
  const allowed = getAdPreviewFormatSetForPlacementKeys(keys);
  if (allowed.size === 0) {
    return baseFormats;
  }
  const filtered = baseFormats.filter((f) => allowed.has(f));
  return filtered.length > 0 ? filtered : baseFormats;
};

/**
 * Resolves a modal row id to a catalog placement key for filtering.
 * @param {string} modalItemId
 * @returns {string}
 */
export const resolveModalPlacementItemToCatalogKey = (modalItemId) =>
  MODAL_PLACEMENT_ITEM_ID_TO_CATALOG_KEY[modalItemId] || modalItemId;
